import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from "@tanstack/react-query";
import type { Alert, AlertFilters, CreateAlert, Match, Plan, PlanStatus } from "@snipe/core";
import { request } from "./api";
import {
  AlertResponse,
  AlertsResponse,
  BurnResponse,
  CancelLoginResponse,
  CreateIdentityResponse,
  IdentitiesResponse,
  MatchesResponse,
  MintResponse,
  NoContent,
  DeleteUserResponse,
  RevokeSessionsResponse,
  RunsResponse,
  StartLoginResponse,
  SummaryResponse,
  SupportUserResponse,
  UserDetailResponse,
  UsersResponse,
  type AlertRun,
  type IdentityHealth,
  type OpsSummary,
  type SupportUser,
  type UserDetail,
} from "./schemas";

/**
 * How often each panel re-reads the server.
 *
 * The summary is the "is it working" screen and is cheap (three filtered
 * aggregates), so it polls fastest. Identities changes on the timescale of a
 * mint. The run log is a log: it grows, and re-reading it every few seconds
 * only moves rows under the cursor of whoever is reading one.
 */
export const SUMMARY_POLL_MS = 15_000;
export const IDENTITY_POLL_MS = 30_000;
export const RUNS_POLL_MS = 60_000;

export const keys = {
  me: ["auth", "me"] as const,
  summary: ["ops", "summary"] as const,
  identities: ["ops", "identities"] as const,
  runs: (filter: RunFilter) => ["ops", "runs", filter.alertId ?? null, filter.limit] as const,
  alerts: ["alerts"] as const,
  userSearch: (query: string) => ["ops", "users", "search", query] as const,
  user: (id: string) => ["ops", "users", id] as const,
};

/** Below this, the API refuses the search anyway; see OpsUserSearchQuery. */
export const USER_SEARCH_MIN = 2;

export interface RunFilter {
  alertId?: string | undefined;
  limit: number;
}

export function useSummary(): UseQueryResult<OpsSummary, Error> {
  return useQuery({
    queryKey: keys.summary,
    queryFn: async () => (await request("/ops/summary", SummaryResponse)).summary,
    refetchInterval: SUMMARY_POLL_MS,
  });
}

export function useIdentities(): UseQueryResult<IdentityHealth[], Error> {
  return useQuery({
    queryKey: keys.identities,
    queryFn: async () => (await request("/ops/identities", IdentitiesResponse)).identities,
    refetchInterval: IDENTITY_POLL_MS,
  });
}

export function useRuns(filter: RunFilter): UseQueryResult<AlertRun[], Error> {
  const query = new URLSearchParams({ limit: String(filter.limit) });
  if (filter.alertId !== undefined && filter.alertId !== "") query.set("alertId", filter.alertId);
  return useQuery({
    queryKey: keys.runs(filter),
    queryFn: async () => (await request(`/ops/runs?${query.toString()}`, RunsResponse)).runs,
    refetchInterval: RUNS_POLL_MS,
  });
}

export function useAlerts(): UseQueryResult<Alert[], Error> {
  return useQuery({
    queryKey: keys.alerts,
    queryFn: async () => (await request("/alerts", AlertsResponse)).alerts,
  });
}

/**
 * One alert's feed, so an operator can create a real alert and watch matches
 * arrive rather than infer from a run count that it probably worked.
 */
export function useAlertMatches(alertId: string | null): UseQueryResult<Match[], Error> {
  return useQuery({
    queryKey: ["alerts", alertId, "matches"] as const,
    queryFn: async () =>
      (await request(`/alerts/${alertId ?? ""}/matches?limit=20`, MatchesResponse)).matches,
    enabled: alertId !== null,
  });
}

export interface BurnInput {
  identityId: string;
  reason?: string | undefined;
}

/**
 * Burning destroys working credentials, so there is no optimistic update here
 * and no local edit of the cached row: the count that comes back is the truth
 * about what happened, and everything else is re-read from the server.
 */
export function useBurnSessions(): UseMutationResult<{ burned: number }, Error, BurnInput> {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ identityId, reason }: BurnInput) =>
      request(`/ops/identities/${identityId}/sessions/burn`, BurnResponse, {
        method: "POST",
        body: reason === undefined || reason === "" ? {} : { reason },
      }),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: keys.identities });
    },
  });
}

/**
 * Mint a session for one identity, now.
 *
 * No `refetchInterval` fights this and no optimistic update pretends: the
 * request BLOCKS for as long as the browser takes, up to ninety seconds, and
 * the mutation's own `isPending` is what the button renders meanwhile. The
 * identities query is invalidated afterwards whatever the outcome -- a
 * failure is recorded against the identity too, and that row is exactly what
 * an operator needs to look at next.
 */
export function useMintSession(): UseMutationResult<MintResponse, Error, string> {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (identityId: string) =>
      request(`/ops/identities/${identityId}/mint`, MintResponse, { method: "POST" }),
    onSettled: () => {
      void client.invalidateQueries({ queryKey: keys.identities });
    },
  });
}

/**
 * Add an identity. A label, and nothing else.
 *
 * No Facebook credential passes through here, or through the API: the login
 * happens in a browser running on the machine that will use the session, so
 * the only thing this request carries is a name an operator typed.
 */
export function useCreateIdentity(): UseMutationResult<CreateIdentityResponse, Error, string> {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (label: string) =>
      request("/ops/identities", CreateIdentityResponse, { method: "POST", body: { label } }),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: keys.identities });
    },
  });
}

/**
 * Start (or rejoin) a live login and get the stream's ticket.
 *
 * Pressing it twice does not start two browsers: the server joins the
 * session already running for that identity and issues a second ticket for
 * it, which is what makes reloading the tab mid-MFA survivable.
 */
export function useStartLogin(): UseMutationResult<StartLoginResponse, Error, string> {
  return useMutation({
    mutationFn: (identityId: string) =>
      request(`/ops/identities/${identityId}/login`, StartLoginResponse, { method: "POST" }),
  });
}

/** Stop a live login and close its browser. Idempotent, like the burn. */
export function useCancelLogin(): UseMutationResult<{ stopped: boolean }, Error, string> {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (identityId: string) =>
      request(`/ops/identities/${identityId}/login/cancel`, CancelLoginResponse, {
        method: "POST",
      }),
    onSettled: () => {
      void client.invalidateQueries({ queryKey: keys.identities });
    },
  });
}

export function useCreateAlert(): UseMutationResult<Alert, Error, CreateAlert> {
  const client = useQueryClient();
  return useMutation({
    mutationFn: async (input: CreateAlert) =>
      (await request("/alerts", AlertResponse, { method: "POST", body: input })).alert,
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: keys.alerts });
    },
  });
}

export interface AlertPatch {
  id: string;
  name?: string;
  filters?: AlertFilters;
  intervalSeconds?: number;
  active?: boolean;
  backfillDays?: number;
}

export function usePatchAlert(): UseMutationResult<Alert, Error, AlertPatch> {
  const client = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, ...patch }: AlertPatch) =>
      (await request(`/alerts/${id}`, AlertResponse, { method: "PATCH", body: patch })).alert,
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: keys.alerts });
    },
  });
}

export function useDeleteAlert(): UseMutationResult<null, Error, string> {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => request(`/alerts/${id}`, NoContent, { method: "DELETE" }),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: keys.alerts });
    },
  });
}

/**
 * Find the customer who emailed in.
 *
 * Disabled below `USER_SEARCH_MIN` characters rather than sent and refused:
 * the server rejects a one-character search because it would match most of
 * the table, and a 400 arriving as somebody types the first letter of an
 * address is a red error message on every visit to this page.
 *
 * No polling. A support screen is read once, acted on, and left.
 */
export function useUserSearch(query: string): UseQueryResult<SupportUser[], Error> {
  const trimmed = query.trim();
  return useQuery({
    queryKey: keys.userSearch(trimmed),
    queryFn: async () =>
      (await request(`/ops/users?q=${encodeURIComponent(trimmed)}`, UsersResponse)).users,
    enabled: trimmed.length >= USER_SEARCH_MIN,
  });
}

export function useUserDetail(id: string | null): UseQueryResult<UserDetail, Error> {
  return useQuery({
    queryKey: keys.user(id ?? ""),
    queryFn: () => request(`/ops/users/${id ?? ""}`, UserDetailResponse),
    enabled: id !== null,
  });
}

export interface PlanChange {
  id: string;
  plan: Plan;
  planStatus: PlanStatus;
}

export function useSetUserPlan(): UseMutationResult<SupportUser, Error, PlanChange> {
  const client = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, plan, planStatus }: PlanChange) =>
      (
        await request(`/ops/users/${id}/plan`, SupportUserResponse, {
          method: "PATCH",
          body: { plan, planStatus },
        })
      ).user,
    onSuccess: (_user, { id }) => {
      void client.invalidateQueries({ queryKey: keys.user(id) });
      void client.invalidateQueries({ queryKey: ["ops", "users", "search"] });
    },
  });
}

/**
 * Sign a customer out everywhere.
 *
 * The detail query is invalidated rather than patched locally: what this
 * changes is whether the customer's own tokens still work, which is not a
 * field on any row this page holds, and pretending otherwise would put a
 * number on screen that came from the button rather than from the server.
 */
export function useRevokeSessions(): UseMutationResult<{ revoked: number }, Error, string> {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      request(`/ops/users/${id}/sessions/revoke`, RevokeSessionsResponse, { method: "POST" }),
    onSuccess: (_result, id) => {
      void client.invalidateQueries({ queryKey: keys.user(id) });
    },
  });
}

/** Close an account. Cascades to their alerts and matches; there is no undo. */
export function useDeleteUser(): UseMutationResult<{ deleted: boolean }, Error, string> {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      request(`/ops/users/${id}`, DeleteUserResponse, { method: "DELETE" }),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: ["ops", "users"] });
    },
  });
}
